import React, { useContext, useRef, useEffect } from 'react';
import classnames from 'classnames';
import ReactDOM from 'react-dom';
import { C as ComboboxContentSearch } from './ComboboxContentSearch-ButRvx3q.js';
import { C as ComboboxContentList } from './ComboboxContentList-DEM2Cj4j.js';
import { C as ComboboxContentHeader } from './ComboboxContentHeader-BLYutXuV.js';
import { a as ComboboxContext } from './ComboboxProvider-DdsdhWRT.js';
import { u as useRefocusOnActivator_2 } from './useRefocusOnActivator-CiCzAHtv.js';
import { u as useFocusTrap_2 } from './useFocusTrap-CsPhaFeT.js';
import { usePopper } from 'react-popper';
import { u as useOnKeyDown_2 } from './useOnKeyDown-t-UhpB2o.js';

var styles = {"content":"m1w5vdUZ6rQ-","hidden":"Zlkv2HA096A-","actions":"YQry-Rd6zfQ-","actionPadding":"TcpqL34s4lI-"};

function useComboboxContent(open, selected) {
    const { shouldScroll } = useContext(ComboboxContext);
    const optionsListRef = useRef(null);
    useEffect(() => {
        var _a;
        if (open && shouldScroll.current && optionsListRef.current) {
            const firstSelected = Array.from((_a = optionsListRef === null || optionsListRef === void 0 ? void 0 : optionsListRef.current) === null || _a === void 0 ? void 0 : _a.children).find(child => {
                if (child instanceof HTMLElement) {
                    return child.dataset.selected === "true";
                }
            });
            scrollToFirstSelected(firstSelected, shouldScroll);
        }
    }, [open, selected]);
    return {
        optionsListRef,
    };
}
function scrollToFirstSelected(firstSelected, shouldScroll) {
    if (firstSelected) {
        firstSelected.scrollIntoView({
            block: "nearest",
        });
        shouldScroll.current = false;
    }
}

// eslint-disable-next-line max-statements
function useComboboxAccessibility(selectionCallback, filteredOptions, optionsListRef, open, wrapperRef) {
    const { handleClose } = useContext(ComboboxContext);
    const hasOptionsVisible = open && filteredOptions.length > 0;
    const focusedIndex = useRef(null);
    useRefocusOnActivator_2(open);
    const popperRef = useFocusTrap_2(open);
    const { styles: popperStyles, attributes, update, } = usePopper(wrapperRef.current, popperRef.current, {
        modifiers: [
            {
                name: "flip",
                options: {
                    fallbackPlacements: ["top-start"],
                },
            },
        ],
        placement: "bottom-start",
    });
    useEffect(() => {
        focusedIndex.current = null;
        if (open) {
            update === null || update === void 0 ? void 0 : update();
        }
    }, [open, filteredOptions.length]);
    useEffect(() => {
        var _a;
        if (open) {
            (_a = popperRef.current) === null || _a === void 0 ? void 0 : _a.addEventListener("keydown", handleContentKeydown);
        }
        return () => {
            var _a;
            (_a = popperRef.current) === null || _a === void 0 ? void 0 : _a.removeEventListener("keydown", handleContentKeydown);
        };
    }, [open, optionsListRef, filteredOptions]);
    useOnKeyDown_2(() => {
        if (open) {
            handleClose();
        }
    }, "Escape");
    function handleContentKeydown(event) {
        var _a;
        if (!hasOptionsVisible)
            return;
        if (event.key === "Enter" || event.key === " ") {
            const activeElementInList = (_a = optionsListRef.current) === null || _a === void 0 ? void 0 : _a.contains(document.activeElement);
            if (!activeElementInList)
                return;
            handleKeyboardSelection(event);
        }
        if (event.key === "ArrowDown") {
            handleKeyboardNavigation(event, 1);
        }
        if (event.key === "ArrowUp") {
            handleKeyboardNavigation(event, -1);
        }
    }
    function handleKeyboardNavigation(event, indexChange) {
        var _a;
        const newIndex = focusedIndex.current === null ? 0 : focusedIndex.current + indexChange;
        if (newIndex < 0 || newIndex >= filteredOptions.length)
            return;
        const optionElement = (_a = optionsListRef.current) === null || _a === void 0 ? void 0 : _a.children[newIndex];
        optionElement === null || optionElement === void 0 ? void 0 : optionElement.focus();
        focusedIndex.current = newIndex;
        event.preventDefault();
        event.stopPropagation();
    }
    function handleKeyboardSelection(event) {
        event.preventDefault();
        event.stopPropagation();
        if (focusedIndex.current !== null) {
            selectionCallback(filteredOptions[focusedIndex.current]);
        }
    }
    return {
        popperRef,
        popperStyles,
        attributes,
    };
}

function ComboboxContent(props) {
    const optionsExist = props.options.length > 0;
    const { optionsListRef } = useComboboxContent(props.open, props.selected);
    const { popperRef, popperStyles, attributes } = useComboboxAccessibility(props.handleSelection, props.options, optionsListRef, props.open, props.wrapperRef);
    const template = (React.createElement("div", Object.assign({ ref: popperRef, id: "ATL-Combobox-Content", "data-testid": "ATL-Combobox-Content", tabIndex: 0, className: classnames(styles.content, { [styles.hidden]: !props.open }), style: popperStyles.popper }, attributes.popper),
        React.createElement(ComboboxContentSearch, { open: props.open, placeholder: props.subjectNoun, searchValue: props.searchValue, setSearchValue: props.setSearchValue, handleSearchChange: props.handleSearchChange }),
        props.multiselect && (optionsExist || props.selected.length > 0) && (React.createElement(ComboboxContentHeader, { hasOptionsVisible: optionsExist, subjectNoun: props.subjectNoun, selectedCount: props.selected.length, onClearAll: () => {
                props.selectedStateSetter([]);
            }, onSelectAll: () => {
                props.selectedStateSetter(props.options);
            } })),
        React.createElement(ComboboxContentList, { multiselect: props.multiselect, options: props.options, selected: props.selected, optionsListRef: optionsListRef, searchValue: props.searchValue, subjectNoun: props.subjectNoun, loading: props.loading, onLoadMore: props.onLoadMore }),
        props.actionElements && (React.createElement("div", { className: styles.actions, role: "group" }, props.actionElements.map((child, index, childrenArray) => (React.createElement("div", { key: index, className: classnames({
                [styles.actionPadding]: index === childrenArray.length - 1,
            }) }, child)))))));
    return (globalThis === null || globalThis === void 0 ? void 0 : globalThis.document)
        ? ReactDOM.createPortal(template, document.body)
        : template;
}

export { ComboboxContent as C };
